"use client";

import type { ProductPage } from "@lib/types";
import { motion } from "framer-motion";

import { CtaFlexible } from "@/components/shared/cta";
import { ResponsiveImage } from "@/components/shared/ResponsiveImage";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/ui/cn";

type ImageTextBannerProps = {
  module: NonNullable<ProductPage["thirdSection"]>["imageTextBanner"];
};

export function ImageTextBanner({ module }: ImageTextBannerProps) {
  if (!module?.title || !module?.body) return null;

  return (
    <section className="py-padding-section-lg md:bg-[linear-gradient(232deg,var(--Color-Neutral-Darkest,#000003)_14.43%,var(--Color-Indigo-Darkest,#13233D)_30.72%,#000212_47.02%)]">
      <Container>
        <div className="gap-gap-element-2xl flex flex-col items-center md:flex-row">
          {module.media ? (
            <div className="relative flex aspect-4/3 w-full overflow-hidden rounded-lg md:w-1/2">
              <ResponsiveImage src={module.media} fill className="object-contain" />
            </div>
          ) : null}

          <div className="pt-padding-section-lg w-full text-left md:w-1/2 md:pt-0">
            <motion.h3
              className="typography-heading-3 mb-gap-element-md text-white"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.0, delay: 0.3, ease: "easeOut" }}
            >
              {module.title}
            </motion.h3>
            <motion.p
              className="typography-heading-4 text-emerald-light"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.0, delay: 0.7, ease: "easeOut" }}
            >
              {module.body}
            </motion.p>
            {"ctas" in module && Array.isArray(module.ctas) && module.ctas.length > 0 && (
              <motion.div
                className={cn(
                  "mt-gap-element-lg gap-gap-element-md flex flex-wrap",
                  "ctaAlignment" in module && module.ctaAlignment === "center"
                    ? "justify-center"
                    : "ctaAlignment" in module && module.ctaAlignment === "right"
                      ? "justify-end"
                      : "justify-start",
                )}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.0, delay: 1.1, ease: "easeOut" }}
              >
                {module.ctas.map((cta, index) => (
                  <div key={index} className="w-fit">
                    <CtaFlexible cta={cta as Parameters<typeof CtaFlexible>[0]["cta"]} />
                  </div>
                ))}
              </motion.div>
            )}
          </div>
        </div>
      </Container>
    </section>
  );
}
